import { useState, useEffect } from 'react';
import { supabase } from '../supabase';

export default function CouponBanner({ couponBanner, user, onGoHome, onOpenAuth }) {
  const [visible,  setVisible]  = useState(false);
  const [status,   setStatus]   = useState('idle'); // idle | loading | success | already | error
  const [errorMsg, setErrorMsg] = useState('');

  const storageKey = `couponBannerDismissed_${couponBanner?.code || 'default'}`;

  useEffect(() => {
    if (!couponBanner?.enabled) return;
    if (localStorage.getItem(storageKey) || sessionStorage.getItem(storageKey)) return;
    const t = setTimeout(() => setVisible(true), (couponBanner.delaySeconds ?? 3) * 1000);
    return () => clearTimeout(t);
  }, [couponBanner, storageKey]);

  const dismissForever = () => { localStorage.setItem(storageKey, '1'); setVisible(false); };
  const dismissSession = () => { sessionStorage.setItem(storageKey, '1'); setVisible(false); };

  const handleClaim = async () => {
    if (!user) {
      setVisible(false);
      onGoHome?.();
      setTimeout(() => onOpenAuth?.(), 100);
      return;
    }
    setStatus('loading');
    try {
      const { data, error } = await supabase.rpc('claim_coupon_by_code', { p_code: couponBanner.code });
      if (error) throw error;
      if (data?.success) {
        setStatus('success');
        setTimeout(() => {
          setVisible(false);
          onGoHome?.();
        }, 2800);
      } else if (data?.error === 'already_claimed') {
        setStatus('already');
      } else {
        setStatus('error');
        setErrorMsg(
          data?.error === 'coupon_not_found' ? '쿠폰을 찾을 수 없습니다' :
          data?.error === 'coupon_expired'   ? '만료된 쿠폰입니다' :
          '오류가 발생했습니다'
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg('오류가 발생했습니다');
    }
  };

  if (!visible) return null;

  const bg  = couponBanner.bgColor    || '#1A1A1A';
  const tc  = couponBanner.textColor  || '#F2F1ED';
  const acc = couponBanner.accentColor || '#C97A3D';

  return (
    <div style={{
      position: 'fixed', bottom: 16, left: 16, zIndex: 3000,
      width: 'min(300px, calc(100vw - 32px))',
      background: bg, color: tc,
      fontFamily: "'Pretendard', sans-serif",
      animation: 'slideInLeft .35s cubic-bezier(.25,.46,.45,.94) both',
      boxShadow: '0 8px 40px rgba(0,0,0,0.32)',
      overflow: 'hidden',
    }}>
        {/* 이미지 */}
        {couponBanner.image && (
          <img
            src={couponBanner.image}
            alt=""
            style={{ width: '100%', display: 'block', maxHeight: 220, objectFit: 'cover' }}
          />
        )}

        {/* 닫기 × */}
        <button
          onClick={dismissSession}
          style={{
            position: 'absolute', top: 12, right: 14,
            background: 'rgba(0,0,0,0.35)', border: 'none', cursor: 'pointer',
            color: '#fff', fontSize: 18, lineHeight: 1,
            width: 28, height: 28, borderRadius: '50%',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1,
          }}
        >×</button>

        {/* 콘텐츠 */}
        <div style={{ padding: '22px 24px 18px' }}>
          {status === 'success' ? (
            <div style={{ textAlign: 'center', padding: '10px 0 8px' }}>
              <div style={{ fontSize: 38, marginBottom: 10 }}>🎉</div>
              <p style={{ fontSize: 16, fontWeight: 700, letterSpacing: .5, marginBottom: 8 }}>
                쿠폰이 발급되었습니다!
              </p>
              <p style={{ fontSize: 12, opacity: .65, lineHeight: 1.7 }}>
                마이페이지 &gt; 내 쿠폰에서 확인하세요.<br />잠시 후 홈으로 이동합니다.
              </p>
            </div>
          ) : status === 'already' ? (
            <div style={{ textAlign: 'center', padding: '10px 0 8px' }}>
              <div style={{ fontSize: 32, marginBottom: 10 }}>📋</div>
              <p style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                이미 발급된 쿠폰입니다
              </p>
              <p style={{ fontSize: 12, opacity: .65 }}>마이페이지에서 확인해 주세요</p>
            </div>
          ) : (
            <>
              <p style={{ fontSize: 30, fontFamily: "'DM Serif Display', serif", color: acc, lineHeight: 1, marginBottom: 8 }}>
                {couponBanner.discountText || '₩3,000 할인'}
              </p>
              <p style={{ fontSize: 14, fontWeight: 700, letterSpacing: .5, marginBottom: 4 }}>
                {couponBanner.title || '신규 회원 혜택'}
              </p>
              {couponBanner.description && (
                <p style={{ fontSize: 12, opacity: .6, lineHeight: 1.7, marginBottom: 16 }}>
                  {couponBanner.description}
                </p>
              )}
              {status === 'error' && (
                <p style={{ fontSize: 12, color: '#ff6b6b', marginBottom: 12 }}>{errorMsg}</p>
              )}
              <button
                onClick={handleClaim}
                disabled={status === 'loading'}
                style={{
                  width: '100%', padding: '13px 0',
                  background: acc, color: '#fff',
                  border: 'none', cursor: status === 'loading' ? 'wait' : 'pointer',
                  fontFamily: "'Pretendard', sans-serif",
                  fontSize: 13, fontWeight: 700, letterSpacing: 1.5,
                  opacity: status === 'loading' ? .7 : 1,
                  transition: 'opacity .15s',
                }}
                onMouseEnter={e => { if (status !== 'loading') e.currentTarget.style.opacity = '.82'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = status === 'loading' ? '.7' : '1'; }}
              >
                {status === 'loading'
                  ? '처리 중...'
                  : user
                    ? (couponBanner.ctaText || '쿠폰 받기')
                    : '로그인하고 받기'}
              </button>
            </>
          )}
        </div>

        {/* 하단 버튼 2개 */}
        <div style={{ display: 'flex', borderTop: `1px solid rgba(255,255,255,0.1)` }}>
          <button
            onClick={dismissForever}
            style={{
              flex: 1, padding: '12px 0',
              background: 'none', border: 'none', cursor: 'pointer',
              color: tc, fontSize: 11, opacity: .45,
              fontFamily: "'Pretendard', sans-serif",
              borderRight: `1px solid rgba(255,255,255,0.1)`,
            }}
          >다시 보지 않기</button>
          <button
            onClick={dismissSession}
            style={{
              flex: 1, padding: '12px 0',
              background: 'none', border: 'none', cursor: 'pointer',
              color: tc, fontSize: 11, opacity: .45,
              fontFamily: "'Pretendard', sans-serif",
            }}
          >닫기</button>
        </div>
    </div>
  );
}
